import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AbstractService } from '../commons/abstract.service';
import { Card, CardBant, CardHealthBand, CardQualification } from "../models/card.model";
import { Contact } from "../models/contact.model";
import { Task } from "../models/task.model";
import { User } from "../models/user.model";
import { SearchParams } from "../commons/abstract.types";
import { Pager } from "../commons/pager";

interface CardAttachment {
  filename: string;
  contentType: string;
  size: number;
  url: string
}

@Injectable({ providedIn: 'root' })
export class CardService extends AbstractService<Card> {
  constructor(private http: HttpClient) {
    super(http);
  }

  getURLBase(): string {
    return '/card';
  }

  preCreate(obj: Card): void {
    delete obj._id;
  }

  preUpdate(obj: Card): void {
  }


  /**
   * Daily-radar reads (SPEC-2026-08-crm-pipeline-deals-radar).
   *
   * All of them go through the inherited `super.get*` helpers — never `this.http`
   * directly, per the service pattern.
   */

  /** Pipeline figures with their variation against the preceding period (RF-01..RF-04). */
  getPipelineSummary(params: Record<string, string | number | boolean>): Observable<PipelineSummary> {
    return super.getWithParams('/pipeline/summary', params);
  }

  /** Conversion per stage, average time in stage and age distribution (RF-21). */
  getPipelineOverview(params: Record<string, string | number | boolean>): Observable<FunnelOverview> {
    return super.getWithParams('/pipeline/overview', params);
  }

  /**
   * Deals outside the ideal customer profile matching the current filters (RF-18).
   * Separate from the pager because `Pager<T>` carries no meta slot.
   */
  getOutOfIcpCount(params: Record<string, string | number | boolean>): Observable<{ count: number }> {
    return super.getWithParams('/icp-count', params);
  }

  /**
   * Count and value per stage for the board's current query (RF-03; SPEC-2026-10 RF-16). Both
   * numbers come from here, because both are FIGURES: a copy is listed in its column by the pager
   * but counted in neither.
   *
   * Pass the SAME params the columns are loading with, otherwise the header and
   * the cards below it describe different populations — which is exactly the bug
   * this endpoint replaced.
   */
  getStageTotals(params: Record<string, string | number | boolean>): Observable<Array<StageTotals>> {
    return super.getWithParams('/stage-totals', params);
  }

  /**
   * The specific origins present in the visible deals, feeding the origin filter
   * (RF-16). Derived from the deals, so the list only ever offers origins that
   * actually produced business in this pipeline.
   */
  getOriginOptions(params: Record<string, string | number | boolean>): Observable<Array<{ value: string; label: string }>> {
    return super.getWithParams('/origins', params);
  }

  /** How the deal's current score was reached (RF-36). */
  getScoreExplanation(idCard: string): Observable<ScoreExplanation> {
    return super.get(`/${idCard}/score-explanation`);
  }

  /** Schedules the deal's next action (RF-12, RF-39). */
  setNextAction(idCard: string, payload: NextActionPayload): Observable<unknown> {
    return super.post(`/${idCard}/next-action`, payload);
  }

  /**
   * What the second step of the conclusion opens with (E4 RF-13, RF-14, RF-16, RF-20 to RF-22):
   * whether a next step is required and the suggested one. Reads only — nothing is created.
   */
  nextStepPreview(idCard: string, idTask: string, params: { outcome: string; idStage?: string }): Observable<NextStepPreview> {
    const query: Record<string, string> = { outcome: params.outcome };
    if (params.idStage) query['idStage'] = params.idStage;
    return super.getWithParams(`/${idCard}/activities/${idTask}/next-step`, query);
  }

  /** Concludes a deal activity — the ONE conclusion command every surface submits (E4 RF-07 to RF-19). */
  concludeActivity(idCard: string, idTask: string, body: ActivityConclusionPayload): Observable<{ task: Task; nextTask?: Task }> {
    return super.post(`/${idCard}/activities/${idTask}/conclusion`, body);
  }

  /**
   * Deletes a deal, settling its open activities as the user chose (E4 RF-51). Without open
   * activities the choice is not needed; with them and no choice, the API refuses and deletes nothing.
   */
  removeDeal(idCard: string, openActivities?: CardRemovalActivitiesAction): Observable<CardRemovalResult> {
    return super.post(`/${idCard}/removal`, openActivities ? { openActivities } : {});
  }

  getCardsPaginated(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Card>> {
    const baseURL = `/pager`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          let urlSearches = '';
          searches.forEach((search) => {
            urlSearches += `&search=${search}`;
          });
          return urlSearches;
        } else {
          return '&search=' + encodeURIComponent(search);
        }
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${encodeURIComponent(`${params[param]}`)}`;
      }
    }
    return super.get(urling);
  }

  getListCardsPaginated(page: number, perPage: number, search?: string, params?: Partial<SearchParams>): Observable<Pager<Card>> {
    const baseURL = `/pager/list`;
    const pager = `${baseURL}?page=${page}&perPage=${perPage}`;

    const getSearchConfig = (search: string): string => {
      if (!!search) {
        if (search.includes('+')) {
          const searches = search.split('+');
          let urlSearches = '';
          searches.forEach((search) => {
            urlSearches += `&search=${search}`;
          });
          return urlSearches;
        } else {
          return '&search=' + encodeURIComponent(search);
        }
      } else {
        return '';
      }
    }
    const searching = `${pager}${getSearchConfig(search)}`;

    let urling = `${searching}`;
    if (!!params) {
      for (let param in params) {
        urling = `${urling}&${param}=${encodeURIComponent(`${params[param]}`)}`;
      }
    }
    return super.get(urling);
  }

  getCardsByFunnel(idFunnel: string): Observable<Array<Card>> {
    return super.get(`/cards/${idFunnel}`);
  }

  getCardsByStage(idFunnel: string, idStage: string): Observable<Array<Card>> {
    return super.get(`/${idFunnel}/stage/${idStage}`);
  }

  updateCardStage(cardId: string, newStageId: string, user: User, idLossReason?: string, presentedAt?: string, lossNote?: string): Observable<void> {
    const userName = user?.name || 'Sistema';
    const payload = {
      newStageId,
      userName,
      idLossReason: idLossReason,
      // Offered when the destination is a presentation stage and the deal has no date yet;
      // the move goes through without it (E2 RF-15, amended 2026-09-16).
      presentedAt,
      // Required when `idLossReason` is a platform discard reason (E3 RF-24).
      lossNote,
    };

    return super.put(`/update-stage/${cardId}`, payload);
  }

  /** E3 RF-01: the qualification a person decided; `level: null` clears it, restoring the recommendation (RF-02). */
  setQualification(idCard: string, level: CardQualification | null, note?: string): Observable<Card> {
    return super.post(`/${idCard}/qualification`, { level, note });
  }

  /** E3 RF-07: any subset of the four qualification facts, each independently reversible. */
  setBant(idCard: string, bant: Partial<CardBant>): Observable<Card> {
    return super.put(`/${idCard}/bant`, bant);
  }

  updateCardPrivacy(idCard: string, isPrivate: boolean): Observable<void> {
    return super.post(`/card-privacy/${idCard}/${isPrivate}`, {});
  }

  updateCardPriority(idCard: string, priority: string): Observable<void> {
    return super.post(`/card-priority/${idCard}/${priority}`, {});
  }

  addRemoveProductToCard(idCard: string, idsProducts: string[]): Observable<Card> {
    return super.post(`/${idCard}/product/`, { idsProducts });
  }

  /**
   * Toggles one tag on the deal — i.e. on the account's PRIMARY CONTACT (E2 RF-27).
   *
   * One call for both directions, like `AttendanceService.addRemoveTag`: the badge's
   * remove icon and the add menu hit the same route. The response is the updated contact
   * with its tags resolved.
   */
  addRemoveTag(idCard: string, idTag: string): Observable<Contact> {
    return super.post(`/${idCard}/tag/${idTag}`, {});
  }

  updateCardResponsibel(idCard: string, user: User): Observable<void> {
    return super.post(`/card-responsible/${idCard}/${user._id}`, {});
  }

  reorderCards(idStage: string, orderedCardIds: Array<string>): Observable<void> {
    return super.post(`/reorder/${idStage}`, { orderedCardIds });
  }

  moveCardToFunnelAndStage(idCard: string, idNewFunnel: string, idNewStage: string, idLossReason?: string, presentedAt?: string, lossNote?: string): Observable<void> {
    // `idLossReason` is required by the API when the destination is a lost stage
    // (RF-29), `presentedAt` when it is a presentation stage (E2 RF-15) and `lossNote`
    // when the reason is a platform discard (E3 RF-24) — same contract as `updateCardStage`.
    return super.put(`/move-to/${idCard}`, { idNewFunnel, idNewStage, idLossReason, presentedAt, lossNote });
  }

  cloneCardToFunnelAndStage(idCard: string, idNewFunnel: string, idNewStage: string): Observable<void> {
    return super.post(`/clone/${idCard}`, { idNewFunnel, idNewStage });
  }

  addAttachment(idCard: string, attachment: CardAttachment): Observable<Card> {
    return super.post(`/${idCard}/attachment`, attachment);
  }

  removeAttachment(idCard: string, attachment: CardAttachment): Observable<Card> {
    return super.put(`/${idCard}/remove-attachment`, attachment)
  }

  getCardByIdAttendance(idAttendance: string): Observable<Card> {
    return super.get(`/attendance/${idAttendance}`);
  }

  /** Every deal of the contact, the one linked to `idAttendance` first (attendance panel). */
  getCardsByContact(idContact: string, idAttendance?: string): Observable<Array<Card>> {
    return super.getWithParams(`/contact/${idContact}`, idAttendance ? { attendance: idAttendance } : {});
  }

  linkAttendance(idCard: string, idAttendance: string): Observable<void> {
    return super.post(`/${idCard}/attendance/${idAttendance}`, {});
  }
}



/** Response of `GET /api/card/pipeline/summary` (RF-01, RF-02, RF-04). */
export interface PipelineSummaryFigure {
  count: number;
  value: number;
  previousCount: number;
  previousValue: number;
  /** null when the preceding period was empty: "grew from nothing" is not a percentage. */
  countVariation: number | null;
  valueVariation: number | null;
}

export interface PipelineSummary {
  /** The period actually read, resolved by the server in the company's timezone (SPEC-2026-10 RF-05). */
  period: { start: string; end: string; timezone?: string };
  previousPeriod: { start: string; end: string };
  open: PipelineSummaryFigure;
  won: PipelineSummaryFigure;
  lost: PipelineSummaryFigure;
}

/** One row of `GET /api/card/stage-totals` (RF-03): the header's two numbers, copies excluded. */
export interface StageTotals {
  stage: string;
  count: number;
  value: number;
}

/** Response of `GET /api/card/pipeline/overview` (RF-21). */
export interface FunnelOverviewStage {
  stage: string;
  name: string;
  order: number;
  dealsReached: number;
  /** null on the last stage and on a stage nobody reached — not the same as 0%. */
  conversionToNext: number | null;
  averageTimeInStageMs: number | null;
  averageTimeInStageSamples: number;
}

/** One row of the loss-reason breakdown (E3 RF-27): a company reason by name, or a platform discard by key (ADR-001). */
export interface FunnelOverviewLossReason {
  /** The row id, or the discard key; null on a deal lost with no reason recorded. */
  reason: string | null;
  platform: boolean;
  /** The company reason's name; null for a platform discard, whose label comes from the catalogs. */
  name: string | null;
  count: number;
  value: number;
}

export interface FunnelOverview {
  period: { start: string; end: string };
  stages: Array<FunnelOverviewStage>;
  ageDistribution: Array<{ band: string; count: number; value: number }>;
  /** Deals lost in the period, by loss reason (E3 RF-27). */
  lossReasons?: Array<FunnelOverviewLossReason>;
}

/** Response of `GET /api/card/:idCard/score-explanation` (RF-36, RF-41). */
export interface ScoreExplanation {
  status: 'OK' | 'UNAVAILABLE';
  reason?: string;
  score: number | null;
  qualification?: CardQualification;
  health?: { score: number; band: CardHealthBand } | null;
  computedAt?: string;
  /** The applicable weight, which is the real denominator (RF-41). */
  availableWeight?: number;
  /** The criteria normalised to 0-100, BEFORE penalties. */
  subtotal?: number;
  /**
   * Penalties applied after the criteria (stagnant, out-of-ICP), as a signed total —
   * negative when they deducted. `subtotal + penaltyTotal` is the score on the card.
   */
  penaltyTotal?: number;
  criteria: Array<{
    criterion: string;
    block: string | null;
    weight: number;
    earned: number;
    applicable: boolean;
  }>;
}

export interface NextActionPayload {
  type: string;
  dueAt: string;
  name?: string;
  description?: string;
  /** A date with no set time (E4 RF-04) — stored at the company-local midnight of that day. */
  untimed?: boolean;
}

/** Response of `GET /api/card/:idCard/activities/:idTask/next-step` (E4 RF-13, RF-14, RF-20 to RF-22). */
export interface NextStepPreview {
  /** Step 2 is required: the deal would stay open with nothing else scheduled (RF-13). */
  required: boolean;
  /** The deal stays open after the requested move; step 2 is offered only then (RF-14). */
  staysOpen: boolean;
  suggestion: {
    type: string;
    /** `task.nextStep.ruleNN` — translated by the dialog; what the user confirms is what is saved (RF-16). */
    titleKey: string;
    rule: number;
    /** The next working day at 09:00 in the company's zone (RF-21, RF-22). */
    dueAt: string;
  };
}

/** What becomes of a deleted deal's open activities (E4 RF-51): concluded — counted nowhere — or deleted. */
export type CardRemovalActivitiesAction = 'CONCLUDE' | 'DELETE';

/** Response of `POST /api/card/:idCard/removal`: how many open activities were settled each way. */
export interface CardRemovalResult {
  concluded: number;
  deleted: number;
}

/** Body of `POST /api/card/:idCard/activities/:idTask/conclusion` (E4 plan AD-02). */
export interface ActivityConclusionPayload {
  outcome: string;
  occurredAt?: string;
  durationMinutes?: number;
  note?: string;
  move?: { idStage: string; idLossReason?: string; lossNote?: string; presentedAt?: string };
  budget?: number;
  next?: NextActionPayload;
  skipReason?: string;
}
